import { getColor } from "../../lib/helper";
import {
  AdditionalInfo,
  TCanvasNode,
  TNodeRecords,
} from "../types/TCanvasNode";
import {
  SearchDisplayMode,
  SearchType,
  TCanvasOptions,
} from "../types/TCanvasOptions";
import { TCanvasLink } from "../types/TCanvasLink";
import { SEMANTIC_MODEL_GROUP_SIZE } from "../../lib/env";

function filterSearchText(
  value: TCanvasNode,
  payload: Record<string, any>
): boolean | null {
  if (payload.searchType == SearchType.SEMANTIC) {
    if (payload.filterItems == null) return null;
    else return payload.filterItems.includes(Number(value.id));
  } else if (payload.searchText.trim() == "") return null;
  else
    return value.name.toLowerCase().includes(payload.searchText.toLowerCase());
}

const nameList: string[] = []


function getNumberFromStringHash(name: string) {
  if (typeof name !== "string" || name.length === 0) {
    return 0; // Or throw an error for invalid input
  }
  const pos = nameList.indexOf(name)
  if(pos == -1) {
    return nameList.push(name) - 1
  } else {
    return pos
  }
}

// function matchesSearchText(searchText: string, value: string) {
//   if (searchText.trim() == "") return false;
//   return value.toLowerCase().includes(searchText.trim().toLowerCase());
// }

function isItemInHiddenList(
  item: TCanvasNode,
  hiddenListSet: Set<string>,
  nodesById: { [k: string]: TCanvasNode }
) {
  if (hiddenListSet.has(item.node_id)) {
    return true;
  } else if (item.parent_node_id && hiddenListSet.has(item.parent_node_id)) {
    return true;
  } else if (item.parent_node_id && nodesById[item.parent_node_id]) {
    return isItemInHiddenList(
      nodesById[item.parent_node_id],
      hiddenListSet,
      nodesById
    );
  } else {
    return false;
  }
}

function getItemColor(
  item: TCanvasNode,
  newRenderNodesById: { [k: string]: TCanvasNode },
  colorIndex?: number
) {
  // Check if node has a custom color in its payload
  if (item.color) {
    return item.color;
  }
  
  if (item.node_type == "DOMAIN") {
    return item.color || getColor(getNumberFromStringHash(item.name));
  } else if (item.node_type == "TABLE") {
    // Use custom color if available
    if (item.color) return item.color;
    
    let parent = item.parent_node_id;
    if (!parent) {
      return getColor(colorIndex);
    }
    let parentNode = newRenderNodesById[parent]
    if(parentNode == undefined) {
      return getColor(colorIndex)
    }
    // Inherit color from parent domain
    return parentNode.color || getColor(getNumberFromStringHash(parentNode.name));
  } else {
    // Column nodes
    if (item.color) return item.color;
    
    let parent = item.parent_node_id;
    if (!parent) {
      return getColor(colorIndex);
    }
    parent = newRenderNodesById[parent].parent_node_id;
    if (!parent) {
      return getColor(colorIndex);
    }
    let grandParentNode = newRenderNodesById[parent]
    if(grandParentNode == undefined) {
      return getColor(colorIndex)
    }
    // Inherit color from grandparent domain
    return grandParentNode.color || getColor(getNumberFromStringHash(grandParentNode.name));
  }
}

// function isPk(item: TCanvasNode) {
//   return item.payload && item.payload.length == 1 && item.payload[0].pk;
// }
//
// function isFk(item: TCanvasNode) {
//   return item.payload && item.payload.length == 1 && item.payload[0].fk;
// }

// type IProps = {
//   domainsById: { [k: string]: TCanvasNode },
//   tablesById: { [k: string]: TCanvasNode },
//   columnsById: { [k: string]: TCanvasNode },
//   prevRenderNodesById: { [k: string]: TCanvasNode },
//   links: TCanvasLink[],
//   connectionsById: { [k: string]: number },
//   canvasOptions: TCanvasOptions | null,
// }

// only select links with high accuracy
function getLinkingNodeById(links: TCanvasLink[]): Record<string, boolean> {
  // nodeid -> has link
  const linking_nodes_by_id: Record<string, boolean> = {};

  // initialize linkingNodesById
  links.forEach((link) => {
    //
    let show = true;
    if (link.accuracy == undefined) show = false;
    else if (link.accuracy < 89) show = false;

    if (typeof link.source == "string") linking_nodes_by_id[link.source] = show;
    else linking_nodes_by_id[link.source.node_id] = show;
    if (typeof link.target == "string") linking_nodes_by_id[link.target] = show;
    else linking_nodes_by_id[link.target.node_id] = show;
  });
  return linking_nodes_by_id;
}

// function addDomains(
//   domainsById: TNodeRecords,
//   hidden_list_set: Set<string>,
//   prevRenderNodesById: TNodeRecords,
//   allNodesById: TNodeRecords,
//   canvasOptions: TCanvasOptions,
//   color_index: number
// ) {
//   const selected_domains: TNodeRecords = {}
//   const { granularity, searchText, searchDisplayMode } = canvasOptions;
//   if (granularity >= 0) {
//     for (const [key, value] of Object.entries(domainsById)) {
//       if (
//         !isItemInHiddenList(value, hidden_list_set, allNodesById)
//       ) {
//         selected_domains[key] = {
//           ...prevRenderNodesById[key],
//           ...value,
//           color: getItemColor(value, selected_domains, color_index++),
//           highlight: value.name
//             .toLowerCase()
//             .includes(searchText.toLowerCase()),
//         };
//       }
//     }
//   }
//
//
//   return selected_domains;
// }

export default function updateNodes(
  domainsById: { [k: string]: TCanvasNode },
  tablesById: { [k: string]: TCanvasNode },
  columnsById: { [k: string]: TCanvasNode },
  prevRenderNodesById: { [k: string]: TCanvasNode },
  links: TCanvasLink[],
  connectionsById: { [k: string]: number },
  canvasOpt: TCanvasOptions | null
): TCanvasNode[] {
  if (canvasOpt == null) return [];
  const newNodes: TCanvasNode[] = []; // final nodes list that  will be drawn

  let colorIndex = 0;

  let {
    granularity,
    maxShowingColumnCount,
    searchText,
    searchType,
    filterItems,
    searchDisplayMode,
    hiddenList,
  } = canvasOpt ?? {
    granularity: 2,
    searchText: "",
    expandOthersNode: false,
    maxShowingColumnCount: 6,
    searchType: SearchType.SEMANTIC,
    searchDisplayMode: SearchDisplayMode.FILTER,
    hiddenList: [],
  }; // canvas options

  const searchPayload = {
    searchText,
    searchType,
    searchDisplayMode,
    filterItems,
  };

  const nodesById = {
    ...domainsById,
    ...tablesById,
    ...columnsById,
  };

  const hidden_list_set = new Set(hiddenList); // set of hidden list
  const linking_nodes_by_id = getLinkingNodeById(links);
  const visible_nodes_by_id: Record<string, boolean> = {};
  // object mapping of node_id to TCanvasNode - this is used to render the final new render nodes list
  let render_nodes_by_id: TNodeRecords = {};
  const connectionPerNodeById: Record<string, AdditionalInfo> = {};

  function linksPerNode() {
    const tableLinks: Record<
      string,
      { interDomain: number; intraDomin: number }
    > = {};
    links.forEach((link) => {
      let src =
        typeof link.source == "string" ? link.source : link.source.node_id;

      let target =
        typeof link.target == "string" ? link.target : link.target.node_id;

      let srcTable = tablesById[columnsById[src].parent_node_id ?? ""];
      let targetTable = tablesById[columnsById[target].parent_node_id ?? ""];
      tableLinks[srcTable.node_id] ??= {
        interDomain: 0,
        intraDomin: 0,
      };
      tableLinks[targetTable.node_id] ??= {
        interDomain: 0,
        intraDomin: 0,
      };

      if (srcTable.parent_node_id == targetTable.parent_node_id) {
        tableLinks[srcTable.node_id].interDomain += 1;
        tableLinks[targetTable.node_id].interDomain += 1;
      } else {
        tableLinks[srcTable.node_id].intraDomin += 1;
        tableLinks[targetTable.node_id].intraDomin += 1;
      }
    });
    return tableLinks;
  }
  const tableLinks = linksPerNode();

  const initializeConnectionPerNode = (id: string) => {
    connectionPerNodeById[id] ??= {
      isVisible: false,
      connections: 0,
      maxChildConnection: 0,
    };
  };

  const updateConnectionPerNode = (_id: string, node: TCanvasNode) => {
    let parent_id =
      node.parent_node_id ??
      (prevRenderNodesById[_id]
        ? prevRenderNodesById[_id].parent_node_id
        : null);
    if (parent_id != null) {
      let connections = 0;
      initializeConnectionPerNode(parent_id);
      connections = connectionPerNodeById[parent_id].connections + 1;
      connectionPerNodeById[parent_id] = {
        ...connectionPerNodeById[parent_id],
        connections,
      };
      let parent = nodesById[parent_id];
      let grandParentId = parent?.parent_node_id;
      if (grandParentId) {
        initializeConnectionPerNode(grandParentId);
        connectionPerNodeById[grandParentId] = {
          ...connectionPerNodeById[grandParentId],
          maxChildConnection: Math.max(
            connections,
            connectionPerNodeById[grandParentId].maxChildConnection
          ),
        };
      }
    }
  };

  const addNodeToRenderNodes = (
    _id: string,
    node: TCanvasNode,
    isSearched: boolean | null,
    nodesPerParentMap: Map<string | number, number>
  ) => {
    const parent = node.parent_node_id;
    const is_highlight_mode =
      canvasOpt.searchDisplayMode == SearchDisplayMode.HIGHLIGHT;
    const is_filtered_mode =
      canvasOpt.searchDisplayMode == SearchDisplayMode.FILTER;
    if (is_filtered_mode) {
      if (
        isSearched == null ||
        isSearched ||
        (node.parent_node_id && visible_nodes_by_id[node.parent_node_id])
      ) {
        render_nodes_by_id[_id] = {
          ...prevRenderNodesById[_id],
          ...node,
          connections: connectionsById[_id],
          color: getItemColor(node, nodesById),
          highlight: isSearched, // by default default
        };
        if (parent) visible_nodes_by_id[parent] = true;
        updateConnectionPerNode(_id, node);
        nodesPerParentMap.set(
          parent ?? "",
          (nodesPerParentMap.get(parent ?? "") ?? 0) + 1
        );
      }
    } else if (is_highlight_mode) {
      render_nodes_by_id[_id] = {
        ...prevRenderNodesById[_id],
        ...node,
        connections: connectionsById[_id],
        color: getItemColor(node, nodesById),
        highlight: isSearched ?? true,
      };
      if (parent) visible_nodes_by_id[parent] = visible_nodes_by_id[parent] || (isSearched ?? true)
      updateConnectionPerNode(_id, node);
      nodesPerParentMap.set(
        parent ?? "",
        (nodesPerParentMap.get(parent ?? "") ?? 0) + 1
      );
    }
  };

  const isHighPriority = (value: TCanvasNode) => {
    return (
      value.parent_node_id == null || // attached to no table
      maxShowingColumnCount == null || // no max nodes value
      value.shown || // user selected it explicitly
      linking_nodes_by_id[value.node_id] // node has a link
    );
  };

  // selected rendering - COLUMNS
  //  - COLUMN NODES
  //  - OTHERS NODES
  if (granularity >= 2) {
    const nodesPerParentMap: Map<string | number, number> = new Map(); // parent_id to count of children nodes mapping
    const otherNodePerTable: { [k: string]: TCanvasNode } = {}; // others node for table
    
    // Create random column limits for each table (3-15 columns, excluding "Others" node)
    const columnLimitsPerTable: Map<string, number> = new Map();
    Object.keys(tablesById).forEach((tableId) => {
      // Random number between 3 and 15 (inclusive)
      const randomLimit = Math.floor(Math.random() * (15 - 3 + 1)) + 3;
      columnLimitsPerTable.set(tableId, randomLimit);
    });

    // add high priority links first
    for (const [key, value] of Object.entries(columnsById)) {
      const isSearched = filterSearchText(value, searchPayload);
      const isHidden = isItemInHiddenList(value, hidden_list_set, nodesById);
      if (!isHidden) {
        if (isSearched) {
          // add all high priority nodes
          addNodeToRenderNodes(key, value, isSearched, nodesPerParentMap);
        }
      }
    }

    for (const [key, value] of Object.entries(columnsById)) {
      const isSearched = filterSearchText(value, searchPayload);
      const isHidden = isItemInHiddenList(value, hidden_list_set, nodesById);
      const isPriority = isHighPriority(value);
      if (!isHidden) {
        if (isPriority && isSearched != true) {
          // add all high priority nodes
          addNodeToRenderNodes(key, value, isSearched, nodesPerParentMap);
        }
      }
    }

    // add low priority links next
    for (const [key, value] of Object.entries(columnsById)) {
      const isSearched = filterSearchText(value, searchPayload);
      const isHidden = isItemInHiddenList(value, hidden_list_set, nodesById);
      const isPriority = isHighPriority(value);
      if (!isHidden) {
        if (!isPriority && isSearched != true) {
          // print remaining low priority nodes
          const count = nodesPerParentMap.get(value.parent_node_id ?? "") ?? 0;
          const parent_node_id = value.parent_node_id;
          // Use per-table random limit instead of global maxShowingColumnCount
          const tableLimit = columnLimitsPerTable.get(parent_node_id ?? "") ?? (maxShowingColumnCount ?? 6);
          const spaceAvailable = count < tableLimit;
          const otherNode = otherNodePerTable[`OTHER_${value.parent_node_id}`];
          if (spaceAvailable) {
            // column node is less than max showing column count for this table
            addNodeToRenderNodes(key, value, isSearched, nodesPerParentMap);
          } else if (otherNode) {
            // add to available other node
            otherNodePerTable[`OTHER_${parent_node_id}`] = {
              ...otherNode,
              payload: [...(otherNode.payload ?? []), value],
            };
          } else {
            // add other node and add this element
            otherNodePerTable[`OTHER_${parent_node_id}`] = {
              node_id: `OTHER_${parent_node_id}`,
              node_type: "OTHER",
              id: newNodes.length,
              parent_node_id: parent_node_id,
              name: "other",
              description: "Other Node",
              payload: [value],
              highlight: null,
            };
          }
        }
      }
    }
    
    // Always add "Others" node for each table that has one
    for (const [key, value] of Object.entries(otherNodePerTable)) {
      render_nodes_by_id[key] = value;
    }
  }

  let sortedTablesKeys = Object.keys(tablesById).sort((a, b) => {
    let aTableLink = tableLinks[a];
    let bTableLink = tableLinks[b];
    if (!aTableLink) {
      return -1;
    }
    if (!bTableLink) {
      return 1;
    }
    let intra = aTableLink.intraDomin - bTableLink.intraDomin;
    return intra != 0 ? intra : aTableLink.interDomain - bTableLink.interDomain;
  });

  const tableLinkIndex: Record<string, number> = {};

  function getNodeIndex(nodeNumber: number) {
    let groupSize = SEMANTIC_MODEL_GROUP_SIZE;
    let index = 1;
    let nodesPassed = 0;
    // console.log(SEMANTIC_MODEL_GROUP_SIZE, "aa")

    while (nodeNumber > nodesPassed + groupSize) {
        nodesPassed += groupSize;
        groupSize *= 2;
        index += 1;
    }

    return index;
  }
  /* ---------------------- TABLES ---------------------- */
  if (granularity >= 1) {
    sortedTablesKeys.forEach((key) => {
      let value = tablesById[key];
      const isHidden = isItemInHiddenList(value, hidden_list_set, nodesById);
      if (isHidden) return; // Skip hidden tables
      
      if(canvasOpt.searchDisplayMode == SearchDisplayMode.HIGHLIGHT) {
        let linkIndex = tableLinkIndex[value.parent_node_id ?? ""] ?? 0;
        render_nodes_by_id[key] = {
          ...prevRenderNodesById[key],
          ...value,
          connections: connectionsById[key],
          color: getItemColor(value, nodesById),
          highlight: visible_nodes_by_id[key],
          linkIndex: getNodeIndex(linkIndex),
        };
        if (value.parent_node_id && visible_nodes_by_id[key])
            visible_nodes_by_id[value.parent_node_id] = true
        updateConnectionPerNode(key, value);
        tableLinkIndex[value.parent_node_id ?? ""] = linkIndex + 1;
      } else {
        if (granularity == 1 || visible_nodes_by_id[key]) {
          let linkIndex = tableLinkIndex[value.parent_node_id ?? ""] ?? 0;
          render_nodes_by_id[key] = {
            ...prevRenderNodesById[key],
            ...value,
            connections: connectionsById[key],
            color: getItemColor(value, nodesById),
            highlight: true,
            linkIndex: getNodeIndex(linkIndex),
          };
          if (value.parent_node_id)
              visible_nodes_by_id[value.parent_node_id] = true
          updateConnectionPerNode(key, value);
          tableLinkIndex[value.parent_node_id ?? ""] = linkIndex + 1;
        }
      }
    });
  }

  if (granularity >= 0) {
    for (const [key, value] of Object.entries(domainsById)) {
      const isHidden = hidden_list_set.has(key);
      if (isHidden) continue; // Skip hidden domains
      
      if(canvasOpt.searchDisplayMode == SearchDisplayMode.HIGHLIGHT) {
        render_nodes_by_id[key] = {
          ...prevRenderNodesById[key],
          ...value,
          color: getItemColor(value, nodesById, colorIndex++),
          highlight: visible_nodes_by_id[key],
          connections: connectionPerNodeById[key]
            ? connectionPerNodeById[key].connections
            : undefined,
          maxChildConnection: connectionPerNodeById[key]
            ? connectionPerNodeById[key].maxChildConnection
            : undefined,
        };
      } else if(canvasOpt.searchDisplayMode == SearchDisplayMode.FILTER) {
        if (granularity == 0 || visible_nodes_by_id[key]) {
          render_nodes_by_id[key] = {
            ...prevRenderNodesById[key],
            ...value,
            color: getItemColor(value, nodesById, colorIndex++),
            highlight: true,
            connections: connectionPerNodeById[key]
              ? connectionPerNodeById[key].connections
              : undefined,
            maxChildConnection: connectionPerNodeById[key]
              ? connectionPerNodeById[key].maxChildConnection
              : undefined,
          };
        }
      }
    }
  }

  return Object.values(render_nodes_by_id);
}
